import React, { useState, useEffect } from 'react';
import { History, Search, Calendar, MessageCircle, Star, Tag, Clock, Download, Trash2, Eye, TrendingUp, Settings, FileText, AlertTriangle, Filter as FilterIcon } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import AIService from '../../services/aiService';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useToast, ToastContainer } from '../common/Toast';
import ConfirmationModal from '../common/ConfirmationModal';

const ChatHistory = () => {
  const navigate = useNavigate();
  const { success, error: showError, info } = useToast();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedChat, setSelectedChat] = useState(null);
  const [chatHistory, setChatHistory] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);

  const categories = [
    { id: 'all', name: 'All Conversations', icon: <MessageCircle size={16} /> },
    { id: 'contracts', name: 'Contracts', icon: <FileText size={16} /> },
    { id: 'assets', name: 'Assets', icon: <TrendingUp size={16} /> },
    { id: 'compliance', name: 'Compliance', icon: <Star size={16} /> },
    { id: 'maintenance', name: 'Maintenance', icon: <Clock size={16} /> },
    { id: 'risk', name: 'Risk', icon: <AlertTriangle size={16} /> },
    { id: 'vendors', name: 'Vendors', icon: <Tag size={16} /> }
  ];

  const fetchChatHistory = async () => {
    setIsLoading(true);
    try {
      const response = await AIService.getChatHistory(selectedCategory, searchTerm);
      if (response.success) {
        setChatHistory(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching chat history:', error);
      showError('Failed to load chat history');
      // Fallback to empty array
      setChatHistory([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchChatHistory();
  }, [selectedCategory, searchTerm]);

  const filteredHistory = chatHistory.filter(chat => {
    const matchesSearch = chat.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         chat.preview?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         chat.tags?.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'all' || chat.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const getCategoryColor = (category) => {
    const colors = {
      contracts: 'var(--primary-color)',
      assets: '#10b981',
      compliance: '#f59e0b',
      maintenance: '#0ea5e9',
      risk: '#ef4444',
      vendors: '#8b5cf6'
    };
    return colors[category] || 'var(--primary-color)';
  };

  const getCategoryGradient = (category) => {
    const gradients = {
      contracts: 'linear-gradient(135deg, var(--primary-color) 0%, #1d4ed8 100%)',
      assets: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      compliance: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      maintenance: 'linear-gradient(135deg, #0ea5e9 0%, #0284c7 100%)',
      risk: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      vendors: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)'
    };
    return gradients[category] || 'linear-gradient(135deg, var(--primary-color) 0%, #1d4ed8 100%)';
  };

  const getCategoryIcon = (category) => {
    const icons = {
      contracts: <FileText size={16} />,
      assets: <TrendingUp size={16} />,
      compliance: <Star size={16} />,
      maintenance: <Clock size={16} />,
      risk: <AlertTriangle size={16} />,
      vendors: <Tag size={16} />
    };
    return icons[category] || <MessageCircle size={16} />;
  };

  const handleDelete = (chat) => {
    setChatToDelete(chat);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!chatToDelete) return;
    
    try {
      await AIService.deleteChatHistory(chatToDelete.id);
      setChatHistory(prev => prev.filter(chat => chat.id !== chatToDelete.id));
      if (selectedChat?.id === chatToDelete.id) {
        setSelectedChat(null);
      }
      setShowDeleteModal(false);
      setChatToDelete(null);
      success('Chat conversation deleted successfully');
    } catch (error) {
      console.error('Error deleting chat:', error);
      showError('Failed to delete chat conversation');
    }
  };

  const handleExport = async () => {
    try {
      // Export all chat history as JSON
      const dataStr = JSON.stringify(chatHistory, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `chat-history-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      success('Chat history exported successfully');
    } catch (error) {
      console.error('Error exporting chat history:', error);
      showError('Failed to export chat history');
    }
  };

  const categoryCounts = categories.map(cat => ({
    ...cat,
    count: cat.id === 'all' ? chatHistory.length : chatHistory.filter(c => c.category === cat.id).length
  }));

  return (
    <>
      <PageBreadcrumb />
      <PageHeader 
        title="Chat History"
        description="Review your previous conversations and saved insights."
        actions={[
          {
            label: 'Export All',
            icon: <Download size={18} />,
            onClick: handleExport,
            variant: 'outline'
          }
        ]}
      />
      
      <div className="page-container">
        {/* Main Content - 2 Column Layout */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', gap: '24px', marginTop: '24px' }}>
          {/* Left Column - Chat List */}
          <div>
            {/* Search and Filter Controls */}
            <div style={{
              backgroundColor: 'white',
              borderRadius: '0',
              padding: '20px',
              marginBottom: '20px',
              boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ position: 'relative', marginBottom: '16px' }}>
                <Search size={20} style={{
                  position: 'absolute',
                  left: '12px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#9ca3af'
                }} />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search conversations, topics, or tags..."
                  style={{
                    width: '100%',
                    padding: '12px 12px 12px 40px',
                    border: '1px solid #d1d5db',
                    borderRadius: '0',
                    fontSize: '14px',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                  }}
                />
              </div>
              
              <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '12px'
              }}>
                <h3 style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: '#374151',
                  margin: 0
                }}>
                  Filter by Category
                </h3>
                <span style={{
                  fontSize: '13px',
                  color: '#6b7280'
                }}>
                  {filteredHistory.length} conversation{filteredHistory.length !== 1 ? 's' : ''}
                </span>
              </div>
              
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                {categoryCounts.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    disabled={isLoading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '8px 14px',
                      backgroundColor: selectedCategory === category.id ? 'var(--primary-color)' : 'transparent',
                      color: selectedCategory === category.id ? 'white' : 'var(--primary-color)',
                      border: `1px solid ${selectedCategory === category.id ? 'var(--primary-color)' : '#d1d5db'}`,
                      borderRadius: '0',
                      fontSize: '13px',
                      fontWeight: '500',
                      cursor: isLoading ? 'not-allowed' : 'pointer',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif',
                      transition: 'all 0.2s',
                      opacity: isLoading ? 0.6 : 1
                    }}
                  >
                    {category.icon}
                    {category.name}
                    <span style={{
                      marginLeft: '4px',
                      padding: '2px 6px',
                      backgroundColor: selectedCategory === category.id ? 'rgba(255,255,255,0.2)' : '#f3f4f6',
                      color: selectedCategory === category.id ? 'white' : '#6b7280',
                      borderRadius: '0',
                      fontSize: '11px'
                    }}>
                      {category.count}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            {/* Chat List */}
            <div style={{
              maxHeight: 'calc(100vh - 400px)',
              overflowY: 'auto',
              paddingRight: '8px'
            }}>
              {isLoading ? (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '0',
                  padding: '60px 20px',
                  textAlign: 'center',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    width: '48px',
                    height: '48px',
                    border: '4px solid #f3f4f6',
                    borderTop: '4px solid var(--primary-color)',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 20px'
                  }} />
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#6b7280',
                    margin: '0 0 8px 0'
                  }}>
                    Loading conversations...
                  </h4>
                </div>
              ) : filteredHistory.length > 0 ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  {filteredHistory.map((chat) => (
                    <div 
                      key={chat.id}
                      onClick={() => setSelectedChat(chat)}
                      style={{
                        backgroundColor: selectedChat?.id === chat.id ? '#f0f9ff' : 'white',
                        border: `2px solid ${selectedChat?.id === chat.id ? 'var(--primary-color)' : '#f1f5f9'}`,
                        borderRadius: '0',
                        padding: '20px',
                        cursor: 'pointer',
                        transition: 'all 0.3s',
                        boxShadow: selectedChat?.id === chat.id ? '0 4px 12px rgba(59, 130, 246, 0.15)' : '0 1px 3px 0 rgba(0, 0, 0, 0.1)'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedChat?.id !== chat.id) {
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = '0 8px 25px rgba(0, 0, 0, 0.1)';
                          e.currentTarget.style.borderColor = '#e2e8f0';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedChat?.id !== chat.id) {
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0, 0, 0, 0.1)';
                          e.currentTarget.style.borderColor = '#f1f5f9';
                        }
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: getCategoryGradient(chat.category),
                          color: 'white',
                          flexShrink: 0
                        }}>
                          {getCategoryIcon(chat.category)}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'flex-start',
                            marginBottom: '8px'
                          }}>
                            <h4 style={{
                              fontSize: '15px',
                              fontWeight: '600',
                              color: '#1f2937',
                              margin: 0
                            }}>
                              {chat.title}
                            </h4>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              {chat.saved && (
                                <Star size={16} style={{ color: '#f59e0b', fill: '#f59e0b' }} />
                              )}
                              <span style={{
                                padding: '4px 10px',
                                backgroundColor: getCategoryColor(chat.category),
                                color: 'white',
                                borderRadius: '0',
                                fontSize: '11px',
                                fontWeight: '500',
                                textTransform: 'uppercase'
                              }}>
                                {chat.category}
                              </span>
                            </div>
                          </div>
                          <p style={{
                            fontSize: '13px',
                            color: '#6b7280',
                            margin: '0 0 8px 0',
                            lineHeight: '1.5'
                          }}>
                            {chat.preview}
                          </p>
                          <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            fontSize: '12px',
                            color: '#9ca3af'
                          }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <Calendar size={12} />
                                {chat.date} • {chat.time}
                              </span>
                              <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                <MessageCircle size={12} />
                                {chat.messages} messages
                              </span>
                            </div>
                            <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              <Clock size={12} />
                              {chat.duration}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div style={{
                  backgroundColor: 'white',
                  borderRadius: '0',
                  padding: '60px 20px',
                  textAlign: 'center',
                  boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{
                    width: '80px',
                    height: '80px',
                    borderRadius: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto 20px',
                    background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                    color: 'white'
                  }}>
                    <History size={32} />
                  </div>
                  <h4 style={{
                    fontSize: '16px',
                    fontWeight: '600',
                    color: '#1f2937',
                    margin: '0 0 8px 0'
                  }}>
                    No conversations found
                  </h4>
                  <p style={{
                    fontSize: '14px',
                    color: '#6b7280',
                    margin: '0 0 20px 0'
                  }}>
                    Try adjusting your search terms or filters.
                  </p>
                  <button 
                    onClick={() => {
                      setSearchTerm('');
                      setSelectedCategory('all');
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      backgroundColor: '#10b981',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontWeight: '600',
                      cursor: 'pointer',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                    }}
                  >
                    <Search size={16} />
                    Clear Filters
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Chat Details */}
          <div>
            {selectedChat ? (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '0',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                height: 'fit-content',
                minHeight: '400px'
              }}>
                <div style={{
                  padding: '24px',
                  borderBottom: '1px solid #e5e7eb',
                  background: 'linear-gradient(135deg, #f8fafc 0%, #ffffff 100%)'
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                      <div style={{
                        width: '56px',
                        height: '56px',
                        borderRadius: '0',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        background: getCategoryGradient(selectedChat.category),
                        color: 'white',
                        flexShrink: 0
                      }}>
                        {getCategoryIcon(selectedChat.category)}
                      </div>
                      <div>
                        <h3 style={{
                          fontSize: '20px',
                          fontWeight: '600',
                          color: '#1f2937',
                          margin: '0 0 12px 0'
                        }}>
                          {selectedChat.title}
                        </h3>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{
                            padding: '6px 12px',
                            backgroundColor: getCategoryColor(selectedChat.category),
                            color: 'white',
                            borderRadius: '0',
                            fontSize: '12px',
                            fontWeight: '500',
                            textTransform: 'uppercase'
                          }}>
                            {selectedChat.category}
                          </span>
                          {selectedChat.saved && (
                            <span style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              color: '#f59e0b',
                              fontSize: '13px',
                              fontWeight: '500'
                            }}>
                              <Star size={16} style={{ fill: '#f59e0b' }} />
                              Saved
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div style={{ position: 'relative' }}>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(selectedChat);
                        }}
                        style={{
                          padding: '8px',
                          backgroundColor: 'transparent',
                          border: '1px solid #d1d5db',
                          borderRadius: '0',
                          cursor: 'pointer',
                          color: '#6b7280'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#fee2e2';
                          e.currentTarget.style.borderColor = '#ef4444';
                          e.currentTarget.style.color = '#ef4444';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = '#d1d5db';
                          e.currentTarget.style.color = '#6b7280';
                        }}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                </div>
                
                <div style={{ padding: '24px' }}>
                  {/* Question */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      margin: '0 0 12px 0'
                    }}>
                      Original Question
                    </h4>
                    <div style={{
                      backgroundColor: '#f8fafc',
                      padding: '16px',
                      borderRadius: '0',
                      border: '1px solid #e5e7eb'
                    }}>
                      <p style={{
                        fontSize: '14px',
                        color: '#374151',
                        margin: 0,
                        lineHeight: '1.6'
                      }}>
                        {selectedChat.question || selectedChat.preview}
                      </p>
                    </div>
                  </div>

                  {/* Summary */}
                  <div style={{ marginBottom: '24px' }}>
                    <h4 style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#374151',
                      margin: '0 0 12px 0'
                    }}>
                      Conversation Summary
                    </h4>
                    <div style={{
                      backgroundColor: 'rgba(59, 130, 246, 0.1)',
                      border: '1px solid rgba(59, 130, 246, 0.2)',
                      padding: '16px',
                      borderRadius: '0'
                    }}>
                      <p style={{
                        fontSize: '14px',
                        color: '#374151',
                        margin: 0,
                        lineHeight: '1.6'
                      }}>
                        {selectedChat.summary || selectedChat.answer || 'No summary available.'}
                      </p>
                    </div>
                  </div>

                  {/* Tags */}
                  {selectedChat.tags && selectedChat.tags.length > 0 && (
                    <div style={{ marginBottom: '24px' }}>
                      <h4 style={{
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#374151',
                        margin: '0 0 12px 0'
                      }}>
                        Topics Discussed
                      </h4>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                        {selectedChat.tags.map((tag, index) => (
                          <span key={index} style={{
                            padding: '6px 12px',
                            backgroundColor: 'rgba(59, 130, 246, 0.1)',
                            color: 'var(--primary-color)',
                            borderRadius: '0',
                            fontSize: '12px',
                            fontWeight: '500'
                          }}>
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Statistics */}
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <div style={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0',
                      padding: '16px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(59, 130, 246, 0.1)',
                          color: 'var(--primary-color)'
                        }}>
                          <MessageCircle size={20} />
                        </div>
                        <div>
                          <div style={{
                            fontSize: '18px',
                            fontWeight: '700',
                            color: '#1f2937'
                          }}>
                            {selectedChat.messages}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280'
                          }}>
                            Messages
                          </div>
                        </div>
                      </div>
                    </div>
                    <div style={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0',
                      padding: '16px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(16, 185, 129, 0.1)',
                          color: '#10b981'
                        }}>
                          <Clock size={20} />
                        </div>
                        <div>
                          <div style={{
                            fontSize: '18px',
                            fontWeight: '700',
                            color: '#1f2937'
                          }}>
                            {selectedChat.duration}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280'
                          }}>
                            Duration
                          </div>
                        </div>
                      </div>
                    </div>
                    <div style={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0',
                      padding: '16px'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '40px',
                          height: '40px',
                          borderRadius: '0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: 'rgba(245, 158, 11, 0.1)',
                          color: '#f59e0b'
                        }}>
                          <Calendar size={20} />
                        </div>
                        <div>
                          <div style={{
                            fontSize: '18px',
                            fontWeight: '700',
                            color: '#1f2937'
                          }}>
                            {selectedChat.date}
                          </div>
                          <div style={{
                            fontSize: '12px',
                            color: '#6b7280'
                          }}>
                            Date
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button
                      onClick={() => navigate('/ai/chat-assistant')}
                      style={{
                        flex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '12px 24px',
                        backgroundColor: 'var(--primary-color)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                      }}
                    >
                      <Eye size={18} />
                      View Full Conversation
                    </button>
                    <button
                      onClick={() => {
                        const dataStr = JSON.stringify(selectedChat, null, 2);
                        const dataBlob = new Blob([dataStr], { type: 'application/json' });
                        const url = URL.createObjectURL(dataBlob);
                        const link = document.createElement('a');
                        link.href = url;
                        link.download = `chat-${selectedChat.id}.json`;
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                        URL.revokeObjectURL(url);
                        success('Chat exported successfully');
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        padding: '12px 24px',
                        backgroundColor: 'transparent',
                        color: 'var(--primary-color)',
                        border: '1px solid var(--primary-color)',
                        borderRadius: '0',
                        fontSize: '14px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                      }}
                    >
                      <Download size={18} />
                      Export
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{
                backgroundColor: 'white',
                borderRadius: '0',
                padding: '60px 40px',
                textAlign: 'center',
                boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1)',
                border: '1px solid #e5e7eb',
                height: 'fit-content',
                minHeight: '400px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <div style={{
                  width: '120px',
                  height: '120px',
                  borderRadius: '0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 24px',
                  background: 'linear-gradient(135deg, var(--primary-color) 0%, #8b5cf6 100%)',
                  color: 'white'
                }}>
                  <MessageCircle size={48} />
                </div>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1f2937',
                  margin: '0 0 12px 0'
                }}>
                  Select a Conversation
                </h4>
                <p style={{
                  fontSize: '14px',
                  color: '#6b7280',
                  margin: '0 0 24px 0',
                  maxWidth: '400px'
                }}>
                  Choose a conversation from the list to view detailed information, 
                  conversation summary, and take actions.
                </p>
                <div style={{ display: 'flex', gap: '12px' }}>
                  <button
                    onClick={() => navigate('/ai/search')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      backgroundColor: 'var(--primary-color)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                    }}
                  >
                    <TrendingUp size={18} />
                    View Analytics
                  </button>
                  <button
                    onClick={() => navigate('/ai/chat-assistant')}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 20px',
                      backgroundColor: 'transparent',
                      color: 'var(--primary-color)',
                      border: '1px solid var(--primary-color)',
                      borderRadius: '0',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif'
                    }}
                  >
                    <Settings size={18} />
                    Settings
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ConfirmationModal
        show={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setChatToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
        title="Delete Conversation"
        message={`Are you sure you want to delete "${chatToDelete?.title}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
      />

      <ToastContainer />
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default ChatHistory;
